// Flusia self-tracking ads (Flusia come advertiser) — lettura del cookie
// first-party `flusia_attribution` scritto da flusia.it (stesso eTLD+1).
// Formato canonico: docs/superpowers/specs/2026-07-16-ads-tracking-funnel-design.md
// NON è il sottosistema ad-attribution multi-tenant dei clienti.

const COOKIE_NAME = 'flusia_attribution';
const COOKIE_MAX_AGE = 90 * 24 * 3600;
const PARAM_KEYS = [
  'gclid', 'gbraid', 'wbraid', 'fbclid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
] as const;

interface Touch { ts: string; landing: string; referrer: string; params: Record<string, string> }
interface ConsentState {
  ad_storage: 'granted' | 'denied'; ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied'; analytics_storage: 'granted' | 'denied';
  updated_at: string;
}
export interface AttributionData {
  v: 1; event_id?: string; first: Touch; last: Touch; last_google?: Touch; consent: ConsentState;
}

function readCookie(): AttributionData | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match.slice(COOKIE_NAME.length + 1)));
    return parsed?.v === 1 && parsed.first?.params && parsed.last?.params ? (parsed as AttributionData) : null;
  } catch { return null; }
}

function writeCookie(data: AttributionData): void {
  const value = encodeURIComponent(JSON.stringify(data));
  document.cookie =
    `${COOKIE_NAME}=${value}; domain=.flusia.it; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`;
}

const deniedConsent = (): ConsentState => ({
  ad_storage: 'denied', ad_user_data: 'denied',
  ad_personalization: 'denied', analytics_storage: 'denied',
  updated_at: new Date().toISOString(),
});

function touchFromCurrentUrl(): Touch {
  const search = new URL(window.location.href).searchParams;
  const params: Record<string, string> = {};
  for (const key of PARAM_KEYS) {
    const value = search.get(key);
    if (value) params[key] = value.slice(0, 512);
  }
  return {
    ts: new Date().toISOString(),
    landing: window.location.href.slice(0, 1024),
    referrer: (document.referrer || '').slice(0, 1024),
    params,
  };
}

/** Sempre non-null: cookie → fallback param URL (Safari ITP) → minimale. */
export function collectAttributionData(): AttributionData {
  const fromCookie = readCookie();
  if (fromCookie) {
    // Ogni touch è una fotografia completa: non eredita campi da altre visite.
    const urlTouch = touchFromCurrentUrl();
    if (Object.keys(urlTouch.params).length > 0) {
      const previousGoogle = fromCookie.last_google ??
        (hasClickParams(fromCookie.last.params) ? fromCookie.last :
          hasClickParams(fromCookie.first.params) ? fromCookie.first : undefined);
      return { ...fromCookie, last: urlTouch,
        last_google: hasClickParams(urlTouch.params) ? urlTouch : previousGoogle };
    }
    return fromCookie;
  }
  const touch = touchFromCurrentUrl();
  return { v: 1, first: touch, last: touch, consent: deniedConsent() };
}

/** Genera event_id (UUID dedicato, MAI il session_token) e lo persiste nel cookie. */
export function ensureEventId(data: AttributionData): AttributionData {
  if (data.event_id) return data;
  const withId = { ...data, event_id: crypto.randomUUID() };
  try { writeCookie(withId); } catch { /* cookie bloccato: id vive solo in questo payload */ }
  return withId;
}

export function getAttributionEventId(): string | null {
  return readCookie()?.event_id ?? null;
}

const GOOGLE_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const hasClickParams = (params: Record<string, string>) => GOOGLE_CLICK_KEYS.some((key) => Boolean(params[key]));

/** Identificativi di click Google dell'ultimo touch (gclid o le varianti iOS/privacy
 *  gbraid/wbraid), nell'ordine di precedenza usato da Google. */
export function getClickIds(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const current = touchFromCurrentUrl().params;
  const data = readCookie();
  // Un nuovo touch esplicito (anche non Google) non va decorato con vecchi ID.
  const params = Object.keys(current).length > 0 ? current :
    Object.keys(data?.last.params ?? {}).length > 0 ? data!.last.params :
      data?.last_google?.params ?? data?.first.params ?? {};
  const out: Record<string, string> = {};
  for (const key of GOOGLE_CLICK_KEYS) {
    if (params[key]) out[key] = params[key];
  }
  return out;
}

/** Vero se c'e' un identificativo di click Google, nell'URL corrente OPPURE nel
 *  cookie.
 *
 *  L'URL va guardato per primo, ed e' il motivo per cui questa funzione esiste
 *  (25/08/2026): chi arriva da un annuncio ha il gclid nella query string, e il
 *  cookie `flusia_attribution` puo' non esistere ancora, perche' lo scrivono
 *  solo il flusso di registrazione e il bridge di consenso. Una guardia basata
 *  sul solo cookie risultava falsa proprio nel caso che deve riconoscere. */
export function hasGoogleClickId(): boolean {
  if (typeof window === 'undefined') return false;
  const search = new URL(window.location.href).searchParams;
  if (GOOGLE_CLICK_KEYS.some((key) => search.get(key))) return true;
  return Object.keys(getClickIds()).length > 0;
}

/** Fotografia corrente se decorata, altrimenti ultimo touch intero conservato.
 *  L'eventuale precedente touch Google rimane separato in last_google. */
export function getAttributionParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = collectAttributionData().last.params;
  const out: Record<string, string> = {};
  for (const key of ['gclid', 'gbraid', 'wbraid', 'fbclid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const) {
    if (params[key]) out[key] = params[key];
  }
  return out;
}

/** Rimette gli identificativi di click nella query string SENZA ricaricare la pagina.
 *
 *  Perché serve (5 ago 2026): la registrazione è un flusso a più passi in una SPA.
 *  L'utente atterra su /register?gclid=..., ma le navigazioni interne fra i passi
 *  perdono la query string; quando alla fine parte la conversione, gtag legge
 *  l'URL corrente e non trova più nulla. Se in più il consenso pubblicitario è
 *  negato non esiste nemmeno il cookie _gcl_aw da cui recuperarlo: la conversione
 *  parte anonima e Google la scarta in silenzio. È esattamente il motivo per cui
 *  "Registrazione Flusia" segnava 0 con registrazioni reali arrivate da annunci.
 *
 *  Non introduce dati nuovi: rimette in circolo un identificativo che il cookie
 *  first-party conserva già. */
export function restoreClickIdsInUrl(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const clickIds = getClickIds();
  if (Object.keys(clickIds).length === 0) return;
  try {
    const url = new URL(window.location.href);
    if (PARAM_KEYS.some((key) => url.searchParams.has(key))) return;
    let changed = false;
    for (const [key, value] of Object.entries(clickIds)) {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
        changed = true;
      }
    }
    if (changed) window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* URL malformato: meglio nessuna attribuzione che un'eccezione nel flusso */
  }
}

/** Iubenda onPreferenceExpressed → riscrive lo stato consenso "ultimo noto" nel
 *  cookie app (gemello di applyConsentPreference in website-next/src/lib/attribution.ts).
 *  Mapping purpose Iubenda: 4 = misurazione → analytics_storage; 5 = marketing → ad_*. */
export function updateConsentFromIubendaPreference(pref: unknown): void {
  const purposes = (pref as { purposes?: Record<string, boolean> } | null)?.purposes;
  if (!purposes) return;
  const data = collectAttributionData();
  const marketing = purposes['5'] === true ? 'granted' as const : 'denied' as const;
  writeCookie({
    ...data,
    consent: {
      ad_storage: marketing, ad_user_data: marketing, ad_personalization: marketing,
      analytics_storage: purposes['4'] === true ? 'granted' : 'denied',
      updated_at: new Date().toISOString(),
    },
  });
}
